/**
 * Grafikr Accessories Module
 * Handles the functionality for adding recommended accessories to cart
 */

import * as ProductUtils from "product-utils";

// Create a global state object for accessories
window.grafikrProductAccessoriesState = {
  selectedAccessories: [],
  hasControlOfButtonText: false,
};

class RecommendedAccessories extends HTMLElement {
  constructor() {
    super();

    try {
      // Find required elements
      this.accessoryCheckboxes = document.querySelectorAll(".grafikr-accessory-checkbox");
      this.productForm = document.getElementById("product-form");
      this.addToCartButton = document.querySelector("[data-add-to-cart]");

      // Initialize state
      this.selectedAccessories = [];
      this.updateTextTimeout = null;
      this.updateTextDelay = 50; // ms delay for debouncing
      this.hasControlOfButtonText = false;
      this.variantObserver = null;

      // Validate required elements
      if (!this.validateRequiredElements()) {
        return;
      }

      // Store initial button text for later reference
      this.initialButtonText = this.addToCartButton.textContent;

      // Initialize the component
      this.init();
    } catch (error) {
      console.error("Error initializing recommended accessories:", error);
    }
  }

  /**
   * Validate that all required elements exist in the DOM
   * @returns {boolean} - Whether all required elements are present
   */
  validateRequiredElements() {
    if (!this.accessoryCheckboxes.length) {
      console.info("No accessory checkboxes found, skipping accessories initialization");
      return false;
    }

    if (!this.productForm) {
      console.info("Product form not found, skipping accessories initialization");
      return false;
    }

    if (!this.addToCartButton) {
      console.info("Add to cart button not found, skipping accessories initialization");
      return false;
    }

    return true;
  }

  /**
   * Initialize the component
   */
  init() {
    try {
      this.handleUpsellStateChange = this.handleUpsellStateChange.bind(this);

      // Set up event listeners for checkboxes
      this.accessoryCheckboxes.forEach((checkbox) => {
        checkbox.addEventListener("change", this.handleCheckboxChange.bind(this));
      });

      // Set up event listeners for clicking on accessory items
      this.setupAccessoryItemClickListeners();

      // Override the form submission
      this.productForm.addEventListener("submit", this.handleFormSubmit.bind(this));

      // Listen for variant changes to update add to cart button text
      document.addEventListener("render:section:product", this.handleVariantChange.bind(this));

      // Listen for product option changes directly
      const optionSelectors = document.querySelectorAll("[data-option]");
      optionSelectors.forEach((option) => {
        option.addEventListener("change", this.handleOptionChange.bind(this));
      });

      // Listen for the product utils button update event
      document.addEventListener("product:buttonTextWillUpdate", this.handleButtonTextWillUpdate.bind(this), false);
      document.addEventListener("product:upsellChanged", this.handleUpsellStateChange);

      // Set up variant change listeners
      this.setupVariantChangeListener();

      // Initialize the Buy Now button
      this.updateBuyNowButton();

      // Initial button text update
      this.updateAddToCartButtonText();

      console.info("Recommended accessories initialized successfully");
    } catch (error) {
      console.error("Error in accessories init:", error);
    }
  }

  getSelectedAddons() {
    if (ProductUtils.getSelectedAddons) {
      return ProductUtils.getSelectedAddons();
    }

    return this.selectedAccessories;
  }

  /**
   * Set up a MutationObserver to watch for variant changes in the product form
   */
  setupVariantChangeListener() {
    // Find the variant selector
    const variantSelector = this.productForm?.querySelector('select[name="id"]');
    if (!variantSelector) return;

    // Create a MutationObserver to watch for changes to the variant selector's value
    this.variantObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === "attributes" && mutation.attributeName === "value") {
          // Variant has changed, update button text
          this.debounceUpdateButtonText();
        }
      }
    });

    // Start observing the variant selector for attribute changes
    this.variantObserver.observe(variantSelector, {
      attributes: true,
      attributeFilter: ["value"],
    });
  }

  /**
   * Handle the product utils button update event
   * @param {CustomEvent} event - The button text update event
   */
  handleButtonTextWillUpdate(event) {
    // If we have selected add-ons and control of the button text,
    // prevent product-utils from updating the button text
    if (this.hasControlOfButtonText && this.getSelectedAddons().length > 0) {
      event.preventDefault();
    }
  }

  /**
   * Handle product option changes
   */
  handleOptionChange() {
    // Update the Buy Now button URL as the product options may affect the variant
    setTimeout(() => {
      // We use a short timeout to ensure the variant selector has been updated
      this.updateBuyNowButton();
    }, 100);

    // Use debounced update instead of directly calling updateAddToCartButtonText
    this.debounceUpdateButtonText();
  }

  /**
   * Handle variant change events
   */
  handleVariantChange() {
    // Re-select our add to cart button as it might have been replaced in the DOM
    this.addToCartButton = document.querySelector("[data-add-to-cart]");

    if (!this.addToCartButton) return;

    // Store the new default text
    this.initialButtonText = this.addToCartButton.textContent;

    // Update the Buy Now button URL as the main product variant may have changed
    this.updateBuyNowButton();

    // Use debounced update
    this.debounceUpdateButtonText();
  }

  handleUpsellStateChange() {
    this.updatePricesWithAccessories();
    this.updateBuyNowButton();
    this.notifyButtonTextState();
    this.debounceUpdateButtonText();
  }

  /**
   * Update price elements when accessories change
   */
  updatePricesWithAccessories() {
    // Get the current selected variant
    const productContainer = document.querySelector("[data-template-product]");
    if (!productContainer) return;

    // Get the product data from the hidden element
    const productDataEl = productContainer.querySelector("[data-data]");
    if (!productDataEl) return;

    try {
      const productData = JSON.parse(productDataEl.textContent);

      // Get current selected options
      let variant = null;

      if (typeof ProductUtils !== "undefined" && ProductUtils.getOptions && ProductUtils.getVariant) {
        const selectedOptions = ProductUtils.getOptions(productContainer, productData);
        variant = ProductUtils.getVariant(productContainer, productData, selectedOptions);
      }

      // If we couldn't find a variant using the options (which happens for linked products),
      // fall back to using the selected_or_first_available_variant from the product data
      if (!variant && productData.selected_or_first_available_variant) {
        variant = productData.selected_or_first_available_variant;
      }

      if (variant) {
        // Update prices using the new function from product-utils
        if (ProductUtils.updatePriceElements) {
          ProductUtils.updatePriceElements(productContainer, variant, this.getSelectedAddons());
        }
      }
    } catch (error) {
      console.error("Error updating prices with accessories:", error);
    }
  }

  /**
   * Handle checkbox change event
   * @param {Event} event - Change event
   */
  handleCheckboxChange(event) {
    const checkbox = event.target;
    const variantId = String(checkbox.dataset.accessoryId).trim(); // Ensure string format and trim whitespace

    // Update the selected class on the parent item for browsers that don't support :has()
    const parentItem = checkbox.closest("[data-accessory-item]");
    if (parentItem) {
      if (checkbox.checked) {
        parentItem.classList.add("selected");
      } else {
        parentItem.classList.remove("selected");
      }
    }

    if (checkbox.checked) {
      // Add to selected accessories
      this.selectedAccessories.push({
        id: variantId,
        title: checkbox.dataset.accessoryTitle,
        price: checkbox.dataset.accessoryPrice,
      });
      this.hasControlOfButtonText = true;
    } else {
      // Remove from selected accessories
      this.selectedAccessories = this.selectedAccessories.filter((item) => String(item.id).trim() !== variantId);
      // If no accessories selected, release control of button text
      if (this.selectedAccessories.length === 0) {
        this.hasControlOfButtonText = false;
      }
    }

    // Update global state
    window.grafikrProductAccessoriesState = {
      selectedAccessories: this.selectedAccessories,
      hasControlOfButtonText: this.hasControlOfButtonText,
    };

    // Update prices to include accessories
    this.updatePricesWithAccessories();

    // Update the Buy Now button URL
    this.updateBuyNowButton();

    // Notify product utils about our state
    this.notifyButtonTextState();

    // Use debounced update
    this.debounceUpdateButtonText();
  }

  /**
   * Update the Buy Now button URL to include selected accessories
   */
  updateBuyNowButton() {
    // Find the Buy Now button
    const buyNowButton = document.querySelector("[data-buy-now]");
    if (!buyNowButton) return;

    // Get the current URL which should already contain the main product
    let currentUrl = buyNowButton.getAttribute("href");

    // If there's no URL or it doesn't point to the cart, exit
    if (!currentUrl || !currentUrl.includes("/cart/")) return;

    // Extract the base cart URL (everything before the variant IDs)
    const baseUrl = currentUrl.split("/cart/")[0] + "/cart/";

    // Get the main product's variant ID by extracting just the first item in the cart
    // Split by '/' and take the part after 'cart/', then split by ',' and take the first item
    let mainProduct = "";
    try {
      const cartPart = currentUrl.split("/cart/")[1];
      // Extract only the first item (main product) by splitting on comma and taking first element
      mainProduct = cartPart.split(",")[0];

      // If there's any query parameters, remove them
      if (mainProduct.includes("?")) {
        mainProduct = mainProduct.split("?")[0];
      }
    } catch (e) {
      console.error("Error extracting main product from URL:", e);
      return;
    }

    // Start building the new cart items string with only the main product
    let cartItems = [mainProduct];

    const selectedAddons = this.getSelectedAddons();

    // Add all selected add-ons to the cart items array (no duplicates)
    if (selectedAddons.length > 0) {
      const addonItems = selectedAddons.map((addon) => {
        return `${this.formatVariantId(addon.id)}:1`;
      });

      cartItems = cartItems.concat(addonItems);
    }

    // Combine all items with commas
    const newCartPart = cartItems.join(",");

    // Create the new complete URL
    const newUrl = baseUrl + newCartPart;

    // Update the Buy Now button href
    buyNowButton.setAttribute("href", newUrl);
  }

  /**
   * Notify product utils about our button text state
   */
  notifyButtonTextState() {
    const selectedAddons = this.getSelectedAddons();

    // Dispatch an event that can be caught by product-utils
    document.dispatchEvent(
      new CustomEvent("accessories:stateChanged", {
        detail: {
          hasAccessories: selectedAddons.length > 0,
          accessoryCount: selectedAddons.length,
          hasControlOfButtonText: this.hasControlOfButtonText,
        },
        bubbles: true,
      })
    );
  }

  /**
   * Debounce the button text update to prevent flickering
   */
  debounceUpdateButtonText() {
    // Clear any existing timeout
    if (this.updateTextTimeout) {
      clearTimeout(this.updateTextTimeout);
    }

    // Set a new timeout
    this.updateTextTimeout = setTimeout(() => {
      this.updateAddToCartButtonText();
      this.updateTextTimeout = null;
    }, this.updateTextDelay);
  }

  /**
   * Update the add to cart button text based on selected accessories
   */
  updateAddToCartButtonText() {
    if (!this.addToCartButton) return;

    // Get the current text for later comparison
    const currentText = this.addToCartButton.textContent;

    // Check if there are any product options that need to be selected
    const optionSelectors = document.querySelectorAll("[data-option]");

    // Check if variant has been selected using the variant selector and button state
    const variantSelector = this.productForm.querySelector('select[name="id"]');

    // We need to determine if a valid variant is selected - this is the key part
    let isVariantSelected = false;

    // Check if all options are selected when options exist
    let allOptionsSelected = true;
    if (optionSelectors.length > 0) {
      // For products with options, we need to check if ALL options are selected
      allOptionsSelected = Array.from(optionSelectors).every((selector) => {
        // Check if this is a radio button group
        if (selector.type === "radio") {
          const optionGroup = document.querySelectorAll(`[data-option="${selector.dataset.option}"]`);
          return Array.from(optionGroup).some((opt) => opt.checked);
        }
        // For other types of selectors (like dropdowns)
        return selector.value && selector.value !== "";
      });
    }

    if (variantSelector && variantSelector.value && variantSelector.value !== "null") {
      if (optionSelectors.length > 0) {
        // For products with options, we need to check if all options are selected
        // and the add button is enabled (meaning it's a valid variant combination)
        isVariantSelected = allOptionsSelected && !this.addToCartButton.disabled;
      } else {
        // For products without options, having a value in the selector is enough
        isVariantSelected = true;
      }
    }

    const selectedAddons = this.getSelectedAddons();
    const totalItems = 1 + selectedAddons.length; // Main product + add-ons

    // Calculate the new text without setting it yet
    let newText = currentText; // Default to no change

    // If we have add-ons selected AND a valid variant is selected,
    // we can update the text to show the count of items
    if (selectedAddons.length > 0 && isVariantSelected) {
      // Check if translation is available
      if (window.theme && window.theme.variantStrings && window.theme.variantStrings.addMultipleToCart) {
        const template = window.theme.variantStrings.addMultipleToCart;
        newText = template.replace("{{ count }}", totalItems);
      } else {
        newText = `Add ${totalItems} to cart`;
      }
      this.hasControlOfButtonText = true;
    }
    // Handle the case where options exist and a variant is selected, but no accessories
    else if (optionSelectors.length > 0) {
      if (isVariantSelected) {
        // If a variant is selected but no accessories, use the standard "Add to cart" text
        if (selectedAddons.length === 0) {
          // Get the standard add to cart text
          if (window.theme && window.theme.variantStrings && window.theme.variantStrings.addToCart) {
            newText = window.theme.variantStrings.addToCart;
          }
          this.hasControlOfButtonText = false;
        }
      } else {
        // If not all options are selected, don't take control of the button text
        this.hasControlOfButtonText = false;
      }
      // If no variant is selected, keep current text (likely "Choose options")
    } else if (selectedAddons.length === 0) {
      // If no options and no accessories, reset to original text
      newText = this.initialButtonText;
      this.hasControlOfButtonText = false;
    }

    // Only update the DOM if the text actually changed
    if (newText !== currentText) {
      // Look for the button text wrapper element first
      const buttonTextElement = this.addToCartButton.querySelector("[data-add-to-cart-text]");

      if (buttonTextElement) {
        // If wrapper exists, update only the text inside it
        buttonTextElement.textContent = newText;
      } else {
        // Fallback to updating the button directly if no wrapper exists
        this.addToCartButton.textContent = newText;
      }

      // Notify product utils about our state after updating
      this.notifyButtonTextState();
    }
  }

  /**
   * Ensure variant ID is properly formatted
   * @param {string|number} id - The variant ID to format
   * @returns {string} - The formatted ID
   */
  formatVariantId(id) {
    // Convert to string and remove any non-numeric characters
    return String(id).replace(/[^0-9]/g, "");
  }

  /**
   * Handle form submission
   * @param {Event} event - Submit event
   */
  handleFormSubmit(event) {
    try {
      event.preventDefault();

      const selectedAddons = this.getSelectedAddons();

      // If no add-ons are selected, handle as a simple product add
      if (selectedAddons.length === 0) {
        this.handleSimpleProductAdd();
        return;
      }

      // Show loading state
      if (this.addToCartButton) {
        this.addToCartButton.classList.add("loading");
        this.addToCartButton.setAttribute("disabled", "disabled");
      }

      // Get the main product variant ID from the select element
      let mainProductVariantId = null;
      const variantSelector = this.productForm?.querySelector('select[name="id"]');

      if (variantSelector) {
        mainProductVariantId = this.formatVariantId(variantSelector.value);
      }

      if (!mainProductVariantId) {
        this.handleErrors({ description: "No product variant selected." });
        // Reset button state
        if (this.addToCartButton) {
          this.addToCartButton.classList.remove("loading");
          this.addToCartButton.removeAttribute("disabled");
        }
        return;
      }

      // Format add-on IDs to ensure consistency
      const formattedAddons = selectedAddons.map((addon) => ({
        ...addon,
        id: this.formatVariantId(addon.id),
      }));

      // Prepare items for cart
      const items = [
        {
          id: mainProductVariantId,
          quantity: 1,
        },
        ...formattedAddons.map((addon) => ({
          id: addon.id,
          quantity: 1,
        })),
      ];

      // Add items to cart using the theme's cart handling
      this.addItemsToCart(items);
    } catch (error) {
      console.error("Error handling form submission:", error);
      this.handleErrors({ description: "An unexpected error occurred. Please try again." });

      // Reset button state
      if (this.addToCartButton) {
        this.addToCartButton.classList.remove("loading");
        this.addToCartButton.removeAttribute("disabled");
      }
    }
  }

  /**
   * Handle simple product addition (no accessories selected)
   */
  handleSimpleProductAdd() {
    try {
      // Show loading state
      if (this.addToCartButton) {
        this.addToCartButton.classList.add("loading");
        this.addToCartButton.setAttribute("disabled", "disabled");
      }

      // Get the main product variant ID from the select element
      let mainProductVariantId = null;
      const variantSelector = this.productForm?.querySelector('select[name="id"]');

      if (variantSelector) {
        mainProductVariantId = this.formatVariantId(variantSelector.value);
      }

      if (!mainProductVariantId) {
        this.handleErrors({ description: "No product variant selected." });
        this.resetButtonState();
        return;
      }

      // Prepare item for cart (just the main product)
      const items = [
        {
          id: mainProductVariantId,
          quantity: 1,
        },
      ];

      // Add to cart
      this.addItemsToCart(items);
    } catch (error) {
      console.error("Error handling simple product add:", error);
      this.handleErrors({ description: "An unexpected error occurred. Please try again." });
      this.resetButtonState();
    }
  }

  /**
   * Add items to cart using the Shopify AJAX API
   * @param {Array} items - Array of items to add
   */
  addItemsToCart(items) {
    if (!items || !items.length) {
      this.handleErrors({ description: "No items to add to cart." });
      this.resetButtonState();
      return;
    }

    // Verify all items have valid IDs before sending
    const validItems = items.filter((item) => {
      const isValid = item.id && String(item.id).length > 0;
      return isValid;
    });

    if (validItems.length === 0) {
      this.handleErrors({ description: "No valid items to add to cart." });
      this.resetButtonState();
      return;
    }

    // Get sections to bundle using the theme's event pattern
    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:grouped-sections", { bubbles: true, detail: { sections: sectionsToBundle } })
    );

    // Use the theme's fetch configuration
    const config = FoxTheme.utils.fetchConfig("javascript");
    config.headers["X-Requested-With"] = "XMLHttpRequest";

    // Create the request body
    config.body = JSON.stringify({
      items: validItems,
      sections: sectionsToBundle,
      sections_url: window.location.pathname,
    });

    try {
      // Use FoxTheme.routes for the URL
      const url = FoxTheme.routes.cart_add_url;

      // Make the fetch request following the theme's pattern
      fetch(url, config)
        .then((response) => response.json())
        .then(async (parsedState) => {
          if (parsedState.status) {
            this.handleErrors(parsedState);
            return;
          }

          // Follow the theme's pattern: fetch cart JSON and merge sections
          const cartJson = await (
            await fetch(`${FoxTheme.routes.cart_url}`, { ...FoxTheme.utils.fetchConfig() })
          ).json();
          cartJson["sections"] = parsedState["sections"];

          // Update cart state using the theme's pubsub system
          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });

          // Dispatch the theme's product added event
          document.dispatchEvent(
            new CustomEvent("product-ajax:added", {
              detail: {
                product: parsedState,
              },
            })
          );

          // Show cart drawer if available (following theme pattern)
          this.showCartDrawer();
        })
        .catch((error) => {
          console.error("Error adding items to cart:", error);
          this.handleErrors({
            description: "An error occurred adding items to your cart. Please try again.",
          });
        })
        .finally(() => {
          this.resetButtonState();
        });
    } catch (error) {
      console.error("Error in cart add request:", error);
      this.handleErrors({ description: "An unexpected error occurred. Please try again." });
      this.resetButtonState();
    }
  }

  /**
   * Show cart drawer following the theme's pattern
   */
  showCartDrawer() {
    // Find the cart drawer element
    const cartDrawerElement = document.querySelector("cart-drawer");

    if (!cartDrawerElement) return;

    // Check if we're in a quick view modal (following theme pattern)
    const quickViewModal = this.addToCartButton?.closest("quick-view-modal");

    if (quickViewModal) {
      if (cartDrawerElement && !cartDrawerElement.open) {
        document.body.addEventListener(
          quickViewModal.events.handleAfterHide,
          () => {
            setTimeout(() => {
              cartDrawerElement.show(this.addToCartButton);
            });
          },
          { once: true }
        );
      }
      quickViewModal.hide(true);
    } else {
      cartDrawerElement.show(this.addToCartButton);
    }
  }

  /**
   * Reset the add to cart button state
   */
  resetButtonState() {
    if (this.addToCartButton) {
      this.addToCartButton.classList.remove("loading");
      this.addToCartButton.removeAttribute("disabled");
    }
  }

  /**
   * Handle errors from cart addition
   * @param {Object} error - Error data
   */
  handleErrors(error) {
    // Display error message
    const errorContainer = this.productForm.querySelector(".product-form__error-message-wrapper");
    const errorMessage = this.productForm.querySelector(".product-form__error-message");

    if (errorContainer && errorMessage) {
      errorMessage.textContent = error.description;
      errorContainer.removeAttribute("hidden");

      // Hide after a delay
      setTimeout(() => {
        errorContainer.setAttribute("hidden", "");
      }, 5000);
    }

    // Dispatch error event using the theme's event name
    document.dispatchEvent(
      new CustomEvent("product-ajax:error", {
        detail: {
          errorMessage: error.description,
        },
      })
    );
  }

  /**
   * Get cart sections for rendering in the response
   * @returns {Array} - Array of section IDs
   */
  getCartSections() {
    // Use the theme's cart:grouped-sections event pattern
    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:grouped-sections", { bubbles: true, detail: { sections: sectionsToBundle } })
    );

    return sectionsToBundle;
  }

  /**
   * Set up click listeners for accessory items
   */
  setupAccessoryItemClickListeners() {
    const accessoryItems = document.querySelectorAll("[data-accessory-item]");

    // First, remove any existing listeners by cloning and replacing each element
    accessoryItems.forEach((item) => {
      // Add clickable class to show it's interactive
      item.classList.add("clickable");

      // Add a data attribute to mark it as having a listener
      if (!item.hasAttribute("data-has-click-listener")) {
        // Add the listener only once
        item.setAttribute("data-has-click-listener", "true");
        item.addEventListener("click", this.handleAccessoryItemClick.bind(this));
      }
    });
  }

  /**
   * Handle clicking on an accessory item
   * @param {Event} event - Click event
   */
  handleAccessoryItemClick(event) {
    // Prevent the event from bubbling up to avoid double-triggering
    event.stopPropagation();

    // Don't trigger if clicking on a link or the checkbox itself
    if (
      event.target.tagName === "A" ||
      event.target.closest("a") ||
      event.target.tagName === "INPUT" ||
      event.target.closest(".grafikr-product__recommended-accessories__checkbox-label")
    ) {
      return;
    }

    // Find the checkbox within this item
    const checkbox = event.currentTarget.querySelector(".grafikr-accessory-checkbox");
    if (checkbox) {
      // Toggle the checkbox
      checkbox.checked = !checkbox.checked;

      // Trigger the change event manually
      const changeEvent = new Event("change", { bubbles: false }); // Set bubbles to false to prevent propagation issues
      checkbox.dispatchEvent(changeEvent);
    }
  }
}

if (!customElements.get("recommended-accessories")) {
  customElements.define("recommended-accessories", RecommendedAccessories);
}

// Initialize the module when the DOM is ready
document.addEventListener("DOMContentLoaded", () => {
  try {
    // Only initialize on product pages
    const isProductPage = document.querySelector("[data-template-product]");
    if (!isProductPage) {
      return;
    }

    // Check if accessories are present
    const hasAccessories = document.querySelectorAll(".grafikr-accessory-checkbox").length > 0;
    if (!hasAccessories) {
      return;
    }
  } catch (error) {
    console.error("Error initializing recommended accessories module:", error);
  }
});
